import type { ApiClient } from '@carbonwebmail/api-client'
import type { OrgRoleDto, TeamDto } from '@carbonwebmail/contracts'
import { useCallback } from 'react'
import type { AsyncState } from '../../ui/Async.js'
import { useAction } from '../../ui/useAction.js'
import { useLoaded } from '../../ui/useLoaded.js'

/**
 * Who is in the organisation, and who has been asked to join.
 *
 * Every action reloads rather than patching a row in place. The server refuses
 * some of them — the last admin cannot be demoted or removed — so guessing the
 * new state here would put a change on screen that never happened.
 */

export type TeamState = AsyncState<TeamDto>

/**
 * What the server said about an invitation, in words.
 *
 * Four of the five are refusals and each needs a different next step, so they
 * are spelled out rather than collapsed into "that did not work".
 */
const INVITE_NOTICES: Record<string, string> = {
  invited: 'Invitation sent.',
  'already-a-member': 'That person is already in your team.',
  'already-invited': 'They already have an invitation waiting. Revoke it first to send another.',
  'address-taken': 'That address already has an account somewhere else.',
  // The only one nobody in the organisation can fix themselves, so it says who
  // can rather than leaving somebody retrying.
  'no-seats':
    'Your plan has no room for another person. Remove somebody, or ask us to move you up a plan.',
}

export function useTeam({ client }: { readonly client: ApiClient }) {
  const load = useCallback(() => client.team(), [client])
  const { state, reload } = useLoaded<TeamDto>(
    load,
    'We could not load your team. Please try again in a moment.',
  )

  const { notice, problem, busy, run } = useAction(reload)

  const invite = useCallback(
    (email: string, role: OrgRoleDto) =>
      run(async () => {
        const outcome = await client.invite({ email, role })
        return INVITE_NOTICES[outcome.status]
      }, 'We could not send that invitation. Check the address and try again.'),
    [client, run],
  )

  const revoke = useCallback(
    (email: string) =>
      run(async () => {
        await client.revokeInvitation(email)
        return 'Invitation revoked. The link in it no longer works.'
      }, 'We could not revoke that invitation. Please try again in a moment.').then(
        () => undefined,
      ),
    [client, run],
  )

  const setRole = useCallback(
    (userId: string, role: OrgRoleDto) =>
      run(
        async () => {
          await client.setMemberRole(userId, { role })
          return undefined
        },
        // The refusal the server actually makes. An organisation with no admin
        // cannot change its own settings, invite anybody, or promote its way
        // out — so the last one cannot be demoted.
        'An organisation needs at least one owner or administrator, so that change was refused.',
      ).then(() => undefined),
    [client, run],
  )

  const remove = useCallback(
    (userId: string) =>
      run(async () => {
        await client.removeMember(userId)
        return 'Removed. Their sessions stop working immediately.'
      }, 'An organisation needs at least one owner or administrator, so that change was refused.').then(
        () => undefined,
      ),
    [client, run],
  )

  return { state, notice, problem, busy, invite, revoke, setRole, remove, retry: reload }
}
